import { CreateSubtenantDto, QueryFactory } from '@lmt/shared-types';

import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { useSubtenant } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useErrorHandler } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { subtenantYupSchema } from './validation';

const defaultSubtenantData: CreateSubtenantDto = {
  name: '',
  code: '', // 9:00
  workingTimeStartInSeconds: 32400, // 9:00
  workingTimeEndInSeconds: 64800, // 18:00
};

type CreateOrEditSubtenantProps = FormComponentProps & {
  id?: number;
};

export const CreateOrEditSubtenant: FC<CreateOrEditSubtenantProps> = (
  props,
) => {
  const { formType, formId, tryCompleteForm } = props;
  const i18next = useScopedTranslation('subtenants.form');
  const queryClient = useQueryClient();

  const subtenantToEdit = props.id
    ? useSubtenant(props.id, { excludeCodeIfSame: false })
    : undefined;
  const { register, handleSubmit, formState, setError, reset, control, watch } =
    useForm<CreateSubtenantDto>({
      resolver: yupResolver(subtenantYupSchema) as any,
      shouldFocusError: true,
      criteriaMode: 'all',
      defaultValues: props.id ? { ...subtenantToEdit } : defaultSubtenantData,
    });

  const [workingTimeStart] = watch(['workingTimeStartInSeconds']);

  const submitForm = useErrorHandler<CreateSubtenantDto>(
    useCallback(
      async (data: CreateSubtenantDto) => {
        await tryCompleteForm(formType, formId, async () => {
          if (props.id) {
            await QueryFactory.TenantClient.patchSubtenant(props.id, data);
          } else {
            await QueryFactory.TenantClient.createSubtenant(data);
          }
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.TenantQuery.getSubtenantsQueryKey(),
          });
        });
      },
      [formType, formId, tryCompleteForm],
    ),
    setError,
    reset,
  );

  return (
    <form
      className={styles.autoSizedContainer}
      onSubmit={handleSubmit(submitForm.handler)}
    >
      <div className={styles.dataContainer}>
        <div className={styles.firstRow}>
          <Field className={styles.firstField} title={i18next.t('name')}>
            <Input
              {...register('name')}
              errorText={formState.errors.name?.message}
              autoFocus={true}
            />
          </Field>
        </div>
        <Field className={styles.field} title={i18next.t('code')}>
          <Input
            {...register('code')}
            errorText={formState.errors.code?.message}
          />
        </Field>
        <div className={styles.row}>
          <Field
            title={i18next.t('working_time_start')}
            className={styles.rowField}
          >
            <Controller
              control={control}
              name={'workingTimeStartInSeconds'}
              render={({ field: { onChange, value } }) => (
                <TimePicker
                  timeInMills={value ? value * 1000 : value}
                  onTimeChanged={(value, isInvalid) =>
                    isInvalid
                      ? onChange(NaN)
                      : onChange(value ? value / 1000 : value)
                  }
                  timeEntriesIntervalInMinutes={30}
                  disableInternalErrors={true}
                  ignoreMinTimeError={true}
                  errorText={
                    formState.errors.workingTimeStartInSeconds?.message
                  }
                />
              )}
            />
          </Field>
          <Field
            title={i18next.t('working_time_end')}
            className={styles.fieldFlex}
          >
            <Controller
              control={control}
              name={'workingTimeEndInSeconds'}
              render={({ field: { onChange, value } }) => (
                <TimePicker
                  timeInMills={value ? value * 1000 : value}
                  onTimeChanged={(value, isInvalid) =>
                    isInvalid
                      ? onChange(NaN)
                      : onChange(value ? value / 1000 : value)
                  }
                  timeEntriesIntervalInMinutes={30}
                  minTimeInMills={
                    workingTimeStart ? workingTimeStart * 1000 : undefined
                  }
                  disableInternalErrors={true}
                  ignoreMinTimeError={true}
                  errorText={formState.errors.workingTimeEndInSeconds?.message}
                  allowMidnightEndTime={true}
                />
              )}
            />
          </Field>
        </div>
      </div>
      <div className={styles.submitContainer}>
        <Error error={submitForm.overallServerError} />
        <SubmitButton
          submitting={formState.isSubmitting}
          className={styles.submit}
          title={i18next.t(props.id ? 'save_button' : 'create_button')}
        />
      </div>
    </form>
  );
};
